import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface Product {
  id: string;
  name: string;
  image: string;
  price: string;
  category: string;
  link: string;
}

const featuredProducts: Product[] = [
  {
    id: '1',
    name: 'Premium Wine Glass Set',
    image: 'https://public.readdy.ai/ai/img_res/9f74c015772844e70cd44a7454c7e4e6.jpg',
    price: '2.50',
    category: 'Glassware',
    link: '/equipment-hire#party-glassware'
  },
  {
    id: '2',
    name: 'Portable Bar Station',
    image: 'https://readdy.ai/api/search-image?query=modern%20portable%20bar%20counter%20black%20finish%20with%20gold%20accents%20professional%20event%20setup%20clean%20white%20background%20luxury%20bar%20equipment%20pristine%20condition%20product%20photography&width=400&height=500&seq=featured-bar-1&orientation=portrait',
    price: '150',
    category: 'Bar Equipment',
    link: '/equipment-hire#portable-bars'
  },
  {
    id: '3',
    name: 'Champagne Flute Collection',
    image: 'https://readdy.ai/api/search-image?query=elegant%20champagne%20flutes%20crystal%20glass%20arranged%20beautifully%20on%20white%20surface%20soft%20lighting%20professional%20product%20photography%20minimalist%20background%20luxury%20stemware%20pristine&width=400&height=500&seq=featured-champagne-1&orientation=portrait',
    price: '3.00',
    category: 'Glassware',
    link: '/equipment-hire#party-glassware'
  },
  {
    id: '4',
    name: 'Cocktail Glass Set',
    image: 'https://readdy.ai/api/search-image?query=premium%20cocktail%20glasses%20martini%20coupe%20glasses%20on%20white%20marble%20professional%20product%20photography%20clean%20minimalist%20background%20luxury%20barware%20pristine%20condition%20elegant&width=400&height=500&seq=featured-cocktail-1&orientation=portrait',
    price: '2.80',
    category: 'Glassware',
    link: '/equipment-hire#party-glassware'
  }
];

export default function FeaturedProducts() {
  const navigate = useNavigate();
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation({ rootMargin: '0px 0px -50px 0px' });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ rootMargin: '0px 0px -30px 0px' });

  const handleAddToQuote = (productId: string) => {
    setAddedItems(prev => new Set(prev).add(productId));
    setTimeout(() => {
      setAddedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }, 2000);
  };

  const handleProductClick = (link: string) => {
    navigate(link);
    setTimeout(() => {
      const hash = link.split('#')[1];
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }, 100);
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white via-amber-50/20 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`text-center mb-16 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <div className="inline-block mb-4">
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-600"></div>
              <i className="ri-star-line text-2xl text-amber-600"></i>
              <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-600"></div>
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-serif text-gray-900 mb-4">
            Featured Products
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Premium quality equipment for your next event. All items professionally cleaned and ready for service.
          </p>
        </div>

        {/* Products Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12 stagger-children ${gridVisible ? 'visible' : ''}`}
        >
          {featuredProducts.map((product) => (
            <div
              key={product.id}
              className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-amber-600/30"
            >
              {/* Product Image */}
              <div
                className="relative h-80 overflow-hidden cursor-pointer"
                onClick={() => handleProductClick(product.link)}
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Category Badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-block px-3 py-1 bg-white/95 backdrop-blur-sm text-xs font-medium text-gray-900 rounded-full">
                    {product.category}
                  </span>
                </div>

                {/* Quick View Icon */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-10 h-10 bg-white/95 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <i className="ri-eye-line text-lg text-gray-900"></i>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className="p-6">
                <h3
                  className="text-xl font-serif text-gray-900 mb-3 group-hover:text-amber-600 transition-colors duration-300 cursor-pointer"
                  onClick={() => handleProductClick(product.link)}
                >
                  {product.name}
                </h3>

                {/* Price */}
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-sm text-gray-500">From</span>
                  <span className="text-2xl font-bold text-amber-600">${product.price}</span>
                  <span className="text-sm text-gray-500">per day</span>
                </div>

                {/* Add to Quote Button */}
                <button
                  onClick={() => handleAddToQuote(product.id)}
                  disabled={addedItems.has(product.id)}
                  className="w-full py-3 px-6 bg-gray-900 text-white font-medium rounded-lg hover:bg-amber-600 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap disabled:bg-green-600 disabled:cursor-not-allowed group/btn"
                >
                  {addedItems.has(product.id) ? (
                    <>
                      <i className="ri-check-line text-lg"></i>
                      <span>Added to Quote</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-add-line text-lg group-hover/btn:rotate-90 transition-transform duration-300"></i>
                      <span>Add to Quote</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* View All Products CTA */}
        <div
          ref={ctaRef}
          className={`text-center scroll-animate ${ctaVisible ? 'visible' : ''}`}
        >
          <button
            onClick={() => navigate('/equipment-hire')}
            className="inline-flex items-center gap-3 px-8 py-4 bg-white border-2 border-gray-900 text-gray-900 font-medium rounded-lg hover:bg-gray-900 hover:text-white transition-all duration-300 whitespace-nowrap group"
          >
            <span>View Full Collection</span>
            <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform duration-300"></i>
          </button>
        </div>

        {/* Trust Note */}
        <div className={`mt-12 text-center scroll-animate ${ctaVisible ? 'visible' : ''}`} style={{ transitionDelay: '150ms' }}>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-amber-50 rounded-full">
            <i className="ri-shield-check-line text-xl text-amber-600"></i>
            <span className="text-sm text-gray-700">
              <strong className="text-gray-900">Limited stock for peak weekends</strong> — Book early to secure your items
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
